import { useMemo } from "react";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { MetricCard } from "@/components/common/MetricCards";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { useAggregateBreachCalculations } from "./useAggregateBreachCalculations";

interface AggregateDeductibleSummaryProps {
  policyId: string;
  aggregateDeductible: number | undefined;
}

const EMPTY = "—";

export const AggregateDeductibleSummary = ({ policyId, aggregateDeductible }: AggregateDeductibleSummaryProps) => {
  const { calculations, isLoading } = useAggregateBreachCalculations(policyId);

  // Latest non-superseded — mirrors BreachSection.tsx pattern.
  const latest = useMemo(() => {
    const sorted = [...calculations].sort(
      (a, b) => new Date(b.created_datetime).getTime() - new Date(a.created_datetime).getTime()
    );
    return sorted.find((c) => !c.is_superseded);
  }, [calculations]);

  const breachAccent = latest ? (latest.is_breached ? "error.main" : "success.main") : "primary.main";
  const headline = latest ? (latest.is_breached ? "Breached" : "Not Breached") : EMPTY;

  const subMetrics = [
    {
      label: "Eligible Spend",
      value: latest ? formatUSD(latest.total_agg_eligible) : EMPTY,
    },
    {
      label: "Aggregate Deductible",
      value: aggregateDeductible ? formatUSD(aggregateDeductible) : EMPTY,
    },
    {
      label: "Carrier Liability",
      value: latest ? formatUSD(latest.is_breached ? latest.carrier_liability : 0) : EMPTY,
    },
  ];

  return (
    <MetricCard
      icon={<WarningAmberOutlined sx={{ fontSize: 18, color: breachAccent }} />}
      title="Aggregate Claim Status"
      value={headline}
      subMetrics={subMetrics}
      isLoading={isLoading}
    />
  );
};
